// ---------------------------------------------------------------------------
// MKT-020 Phase 2 — Integration Marketplace install / health / disconnect
//                   CQRS Event Catalog (vocabulary tier)
// ---------------------------------------------------------------------------
// Story MKT-020: "Integration marketplace shows available integrations,
// health, connect/settings actions and AI recommendations." (F-147)
//
// Code reality on `origin/main` (per
// `MARKETPLACE-INTEGRATIONS-ECOSYSTEM-ATOMIC-STORY-REGISTRY.md`, 2026-05-15):
//
//   - `workers/api-platform/src/routes/integrations/connections.ts` exposes
//     `GET /connections` (list) + `DELETE /connections/:id` (disconnect).
//   - `packages/db/src/schema/integration-connections.ts` ships the row
//     (`INTEGRATION_AUTH_TYPES`, `INTEGRATION_STATUSES`) — the Phase 1
//     schema this catalog binds against.
//   - OAuth callbacks live under `workers/api-platform/src/routes/
//     integrations/*-oauth.ts` (LinkedIn / Meta / Pinterest / TikTok /
//     Twitter / YouTube — all merged via PRs #700, #703, #722, #724, #863).
//   - The integration-recommendation surface is on
//     `test/mkt-020-integration-recommendations-proof` (in_progress).
//   - Repo-wide grep for `marketplace.integration.installed|integration.
//     health_changed|integration.disconnected` returns ZERO non-decision-
//     packet hits.
//
// This file is the Phase 2 narrow follow-up: it catalogues the 4 CQRS event
// types the install / health / disconnect / recommendation surface MUST emit
// so the future workflow-engine triggers + analytics + recommendation engine
// can `import` the strings + payload types instead of hand-rolling them.
//
// Per CLAUDE.md Governance Rule #7 ("Event Catalog"): every event type MUST
// be catalogued before implementation. This file locks the single source of
// truth so the emitter PRs can build against these constants.
//
// What this file is NOT:
//  - It does not declare `integration_connections` / `integration_health`
//    tables — `integration_connections` lives in Phase 1
//    `packages/db/src/schema/integration-connections.ts`.
//  - It does not emit events — that's the connections / oauth-callback /
//    health-check tranche.
//  - It does not declare HTTP routes — those live in `workers/api-platform`.
//  - It does NOT enumerate every provider id — `providerId` stays a free
//    string sourced from `integration_connections.provider_id` so a new
//    integration onboarding does NOT force a catalog bump. The provider
//    ALLOWLIST is enforced at the marketplace listing tier, not at this
//    event tier.
//
// What it IS:
//  - The single source of truth for the 4 catalogued event type strings,
//    all under the `marketplace.integration.*` namespace (kept distinct
//    from MKT-019's `developer.app.*` namespace — those are vendor-supplied
//    apps in the developer portal, while MKT-020 is operator-facing
//    integration management).
//  - The closed vocabulary for `installSource` so analytics can separate
//    in-product install (browse → connect) from MCP-driven install from
//    AI-recommendation acceptance.
//  - The closed vocabulary for `healthStatus` — MIRRORED VERBATIM from
//    Phase 1 `INTEGRATION_STATUSES` in `integration-connections.ts` so
//    the projection cannot drift from the row column. If the schema
//    vocab is ever widened, this catalog MUST be bumped to `@v2` in
//    lockstep.
//  - The closed vocabulary for `disconnectReason` so reconnection prompts
//    + GDPR audit can distinguish user-triggered from provider-revoked
//    from token-expired from GDPR-erasure disconnections.
//  - Typed payload contracts so emitters + DLQ + audit-runner cannot drift
//    on connectionId / providerId / tenantId / actorId shape.
//  - Runtime narrowing guards so DLQ + audit-runner reject unknown event
//    types, install sources, health statuses, and disconnect reasons at
//    the boundary.
// ---------------------------------------------------------------------------

import { INTEGRATION_STATUSES } from "./integration-connections";

/**
 * Closed catalog of MKT-020 CQRS event types. Versioned with `@v1`
 * suffix per platform convention (Governance Rule #7) so future shape
 * changes ship a `@v2` alongside (never mutate in place).
 *
 * One business fact per event (Governance Rule #6):
 *  - `marketplace.integration.installed@v1`        — operator (or MCP
 *    agent / AI recommendation acceptance) connected a new integration.
 *    Audit anchor for "who connected which integration via which entry
 *    point and when". Drives the OAuth-token-refresh scheduler enrolment.
 *  - `marketplace.integration.health_changed@v1`   — health probe
 *    transitioned the connection from one `INTEGRATION_STATUSES` value
 *    to another (e.g., `active` → `expired`, `error` → `active` after
 *    a successful refresh). Drives the in-product "needs attention"
 *    badge + the operator alert digest.
 *  - `marketplace.integration.disconnected@v1`     — operator (or system)
 *    disconnected the integration. Distinct from `health_changed` ending
 *    in `revoked` — disconnection is a one-way write that removes
 *    encrypted credentials; revocation is a probe-detected provider-side
 *    state. Carries `disconnectReason` so reconnection prompts can be
 *    routed differently per reason.
 *  - `marketplace.integration.recommended@v1`      — AI recommendation
 *    engine surfaced an integration suggestion to the operator. Drives
 *    the recommendation-adoption funnel (recommended → installed) and
 *    the recommendation suppression schedule (do not re-recommend the
 *    same provider for 30 days after a dismissal).
 */
export const MKT_020_INTEGRATION_MARKETPLACE_EVENT_TYPES = [
  "marketplace.integration.installed@v1",
  "marketplace.integration.health_changed@v1",
  "marketplace.integration.disconnected@v1",
  "marketplace.integration.recommended@v1",
] as const;

export type Mkt020IntegrationMarketplaceEventType =
  (typeof MKT_020_INTEGRATION_MARKETPLACE_EVENT_TYPES)[number];

/**
 * Closed catalog of install sources. Drives the install-funnel breakdown:
 *  - `marketplace_browse`     — operator clicked Connect from the
 *    marketplace browse page (default entry-point).
 *  - `recommendation_accept`  — operator accepted an AI recommendation
 *    via the recommendation surface (the recommendation event for this
 *    install will have the same `providerId`).
 *  - `mcp`                    — MCP tool invocation by an automation
 *    agent (operator-authorised tool call).
 *  - `onboarding_wizard`      — onboarding flow auto-connected a
 *    recommended integration (operator approved during onboarding).
 *  - `api_direct`             — operator called the public REST API
 *    directly (Postman / curl / their own integration).
 */
export const MKT_020_INSTALL_SOURCE_VOCAB = [
  "marketplace_browse",
  "recommendation_accept",
  "mcp",
  "onboarding_wizard",
  "api_direct",
] as const;

export type Mkt020InstallSourceVocab =
  (typeof MKT_020_INSTALL_SOURCE_VOCAB)[number];

/**
 * Closed health-status vocabulary. MIRRORED VERBATIM from Phase 1
 * `INTEGRATION_STATUSES` in `integration-connections.ts` so the audit
 * projection cannot drift from the row column. The drift-guard test
 * pins this against the Phase 1 source array — if `INTEGRATION_STATUSES`
 * is ever widened, this catalog MUST bump to `@v2` in lockstep.
 *
 * Phase 1 vocabulary (re-exported for emitter ergonomics):
 *  - `active`  — credentials valid, last health probe succeeded.
 *  - `expired` — access token expired, refresh token still valid.
 *  - `error`   — last probe failed for a non-credential reason
 *    (provider rate-limit, 5xx, etc.). Retried on schedule.
 *  - `revoked` — provider revoked the credential (e.g., user revoked
 *    OAuth at the provider's account portal). Reconnection required.
 *  - `pending` — connection created but first health probe not yet
 *    run; transitional state.
 */
export const MKT_020_HEALTH_STATUS_VOCAB = [
  ...INTEGRATION_STATUSES,
] as const;

export type Mkt020HealthStatusVocab =
  (typeof MKT_020_HEALTH_STATUS_VOCAB)[number];

/**
 * Closed catalog of disconnect reasons. Drives the reconnection prompt
 * routing + GDPR audit:
 *  - `user_initiated`     — operator clicked Disconnect in the UI.
 *    Default prompt: "Reconnect when you're ready."
 *  - `provider_revoked`   — provider returned 401/403 on the last probe
 *    AND the refresh token is also rejected. Default prompt: "Your
 *    provider session was ended; reconnect to resume sync."
 *  - `token_expired`      — both access and refresh tokens expired
 *    (typically because refresh window elapsed before next sync).
 *    Default prompt: "Reconnect to restore your integration."
 *  - `tenant_offboarding` — tenant offboarding cascade. No reconnection
 *    prompt — connection is permanently gone with the tenant data.
 *  - `gdpr_erasure`       — DSAR erasure handler removed the connection
 *    as part of right-to-erasure. No reconnection prompt — re-enabling
 *    the connection would violate the erasure obligation.
 */
export const MKT_020_DISCONNECT_REASON_VOCAB = [
  "user_initiated",
  "provider_revoked",
  "token_expired",
  "tenant_offboarding",
  "gdpr_erasure",
] as const;

export type Mkt020DisconnectReasonVocab =
  (typeof MKT_020_DISCONNECT_REASON_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Runtime narrowing guard. DLQ consumers and the audit-runner use this
 * to reject unknown event types at the boundary instead of silently
 * fanning them downstream.
 */
export function isMkt020IntegrationMarketplaceEventType(
  value: unknown,
): value is Mkt020IntegrationMarketplaceEventType {
  return (
    typeof value === "string" &&
    (
      MKT_020_INTEGRATION_MARKETPLACE_EVENT_TYPES as readonly string[]
    ).includes(value)
  );
}

/**
 * Runtime narrowing guard for the install-source vocab. The install-funnel
 * breakdown + AI-recommendation adoption attribution both key off this
 * value; a free-form source would silently land in an "unknown" bucket.
 */
export function isMkt020InstallSourceVocab(
  value: unknown,
): value is Mkt020InstallSourceVocab {
  return (
    typeof value === "string" &&
    (MKT_020_INSTALL_SOURCE_VOCAB as readonly string[]).includes(value)
  );
}

/**
 * Runtime narrowing guard for the health-status vocab. The "needs
 * attention" badge + operator alert digest both key off this value;
 * an unknown status would silently fall through the badge gate.
 */
export function isMkt020HealthStatusVocab(
  value: unknown,
): value is Mkt020HealthStatusVocab {
  return (
    typeof value === "string" &&
    (MKT_020_HEALTH_STATUS_VOCAB as readonly string[]).includes(value)
  );
}

/**
 * Runtime narrowing guard for the disconnect-reason vocab. Reconnection
 * prompt routing + GDPR audit both key off this value; an unknown reason
 * would silently fall through the default reconnection-prompt path.
 */
export function isMkt020DisconnectReasonVocab(
  value: unknown,
): value is Mkt020DisconnectReasonVocab {
  return (
    typeof value === "string" &&
    (MKT_020_DISCONNECT_REASON_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contract                                                 */
/* -------------------------------------------------------------------------- */
// Mirrors the existing `integration_connections` schema column names
// (`connection_id` → `connectionId`, `provider_id` → `providerId`,
// `tenant_id` → `tenantId`). The recommendation funnel + reconnection
// prompts read these directly from the event payload without
// re-querying.

/**
 * Payload emitted when an integration is connected. Audit anchor for
 * "who connected which integration via which entry point and when".
 * Drives OAuth-token-refresh scheduler enrolment + the install-funnel
 * dashboard.
 */
export interface Mkt020IntegrationInstalledPayload {
  /** Stable id of the `integration_connections` row. */
  connectionId: string;
  /**
   * Free-form provider id sourced from `integration_connections.
   * provider_id` (e.g., `hubspot`, `salesforce`, `ghl`, `meta_pages`).
   * NOT enumerated in this catalog — the marketplace listing tier
   * owns the provider allowlist.
   */
  providerId: string;
  /** Tenant scope. */
  tenantId: string;
  /** Closed-list entry-point dimension. */
  installSource: Mkt020InstallSourceVocab;
  /** Operator user id who initiated the install (audit anchor). */
  actorId: string;
  /**
   * For `recommendation_accept` source: id of the
   * `marketplace.integration.recommended@v1` event the operator
   * accepted. `null` for all other sources.
   */
  recommendationId: string | null;
  /** ISO-8601 — when the install landed. */
  installedAt: string;
}

/**
 * Payload emitted when a health probe transitions the connection to a
 * new status. Idempotency anchor: `(connectionId, changedAt)` MUST be
 * unique per transition — the probe MUST NOT emit on no-op transitions.
 * Drives the in-product "needs attention" badge.
 */
export interface Mkt020IntegrationHealthChangedPayload {
  /** Stable id of the affected connection. */
  connectionId: string;
  /** Provider id (audit anchor). */
  providerId: string;
  /** Tenant scope. */
  tenantId: string;
  /** Closed-list previous status (mirrored from `INTEGRATION_STATUSES`). */
  previousStatus: Mkt020HealthStatusVocab;
  /** Closed-list new status (mirrored from `INTEGRATION_STATUSES`). */
  newStatus: Mkt020HealthStatusVocab;
  /**
   * Probe error message, if any. Constrained to ≤ 500 chars; safe to
   * fan out to the operator alert digest. NEVER contains the access
   * or refresh token (probe code strips before emitting).
   */
  probeError: string | null;
  /** ISO-8601 — when the transition landed. */
  changedAt: string;
}

/**
 * Payload emitted when a connection is disconnected (user, system, or
 * compliance-driven). Distinct from `health_changed` ending in `revoked`
 * — disconnection removes encrypted credentials, while revocation only
 * flags the row.
 */
export interface Mkt020IntegrationDisconnectedPayload {
  /** Stable id of the disconnected connection. */
  connectionId: string;
  /** Provider id (audit anchor). */
  providerId: string;
  /** Tenant scope. */
  tenantId: string;
  /** Closed-list disconnect reason (drives reconnection-prompt routing). */
  disconnectReason: Mkt020DisconnectReasonVocab;
  /**
   * Operator user id who triggered the disconnect. `null` for
   * `tenant_offboarding`, `gdpr_erasure`, and `provider_revoked`
   * (system-triggered cascade).
   */
  actorId: string | null;
  /** ISO-8601 — when the disconnect landed. */
  disconnectedAt: string;
}

/**
 * Payload emitted when the AI recommendation engine surfaces an
 * integration suggestion to the operator. Drives the recommendation-
 * adoption funnel (recommended → installed) and the recommendation
 * suppression schedule.
 */
export interface Mkt020IntegrationRecommendedPayload {
  /** Stable id of the recommendation row. */
  recommendationId: string;
  /** Provider id being recommended (free-form, see `installed` note). */
  providerId: string;
  /** Tenant scope. */
  tenantId: string;
  /**
   * Operator user id the recommendation was surfaced to. The
   * recommendation engine is per-operator (not per-tenant) so an
   * operator-scoped dismissal cannot block the recommendation for
   * teammates.
   */
  recipientUserId: string;
  /**
   * AI confidence score in `[0, 1]` — operator dashboards sort the
   * recommendation list by this value.
   */
  confidence: number;
  /**
   * Free-form rationale string, ≤ 500 chars. Safe to surface to the
   * operator verbatim. Never contains tenant PII (engine scrubs
   * before emitting).
   */
  rationale: string;
  /** ISO-8601 — when the recommendation was surfaced. */
  recommendedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (audit-runner, DLQ, install-funnel dashboard, recommendation engine,
 * reconnection-prompt scheduler) can `switch (event.type)` and pull
 * the right payload shape without any-casting.
 */
export type Mkt020IntegrationMarketplaceEvent =
  | {
      type: "marketplace.integration.installed@v1";
      payload: Mkt020IntegrationInstalledPayload;
    }
  | {
      type: "marketplace.integration.health_changed@v1";
      payload: Mkt020IntegrationHealthChangedPayload;
    }
  | {
      type: "marketplace.integration.disconnected@v1";
      payload: Mkt020IntegrationDisconnectedPayload;
    }
  | {
      type: "marketplace.integration.recommended@v1";
      payload: Mkt020IntegrationRecommendedPayload;
    };
